import {Button, Checkbox, FormControlLabel, Grid, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useCallback, useState} from "react";
import {useNavigate, useParams} from "react-router";
import InfoBox from "../../components/InfoBox";
import {useApplicationStorage} from "../../hooks/useApplicationStorage";
import {useGetCountries} from "../../hooks/useGetCountries";
import Preloader from "../../components/Preloader/index";
import {useGetGenders} from "../../hooks/useGetGenders";
import {formatTo} from "../../utils/dateTimeHelper";
import {useGetStartDates} from "../../hooks/useGetStartDates";
import {useGetGrades} from "../../hooks/useGetGrades";
import {useGetLanguages} from "../../hooks/useGetLanguages";
import {useGetNationalities} from "../../hooks/useGetNationalities";
import {useGetEthnicities} from "../../hooks/useGetEthnicities";
import {useGetRelationships} from "../../hooks/useGetRelationships";
import ConfirmationParentIcon from "../../assets/icons/confirmation-parent.svg?react";
import ConfirmationStudentIcon from "../../assets/icons/confirmation-student.svg?react";
import {ROUTES} from "../../router/config";
import {useApplicationSubmit} from "../../hooks/useApplicationSubmit";
import {useGetPerformanceAreas} from "../../hooks/useGetPerformanceAreas";
import "./ConfirmationPage.css";

const ConfirmationPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {applicationId} = useParams();

    const [termsChecked, setTermsChecked] = useState(false);
    const [showError, setShowError] = useState(false);

    const {leadData, studentsData, clear} = useApplicationStorage();

    const {submitApplication, isSubmittingApplication} = useApplicationSubmit(applicationId);

    const {isLoading: isLoadingCountries, phoneCodeOptions, countriesOptions} = useGetCountries();
    const {isLoading: isLoadingGenders, gendersOptions} = useGetGenders();
    const {isLoading: isLoadingStartDates, startDatesOptions} = useGetStartDates();
    const {isLoading: isLoadingGrades, gradesOptions} = useGetGrades();
    const {isLoading: isLoadingPerformanceAreas, performanceAreasOptions} = useGetPerformanceAreas();
    const {isLoading: isLoadingLanguages, languagesOptions} = useGetLanguages();
    const {isLoading: isLoadingNationalities, nationalitiesOptions} = useGetNationalities();
    const {isLoading: isLoadingEthnicities, ethnicitiesOptions} = useGetEthnicities();
    const {isLoading: isLoadingRelationships, relationshipsOptions} = useGetRelationships();

    const formatName = useCallback((person = {}) => {
        return `${person.title ? `${person.title} ` : ""}${person.forename} ${person.surname}${person.suffix ? ` ${person.suffix}` : ""}`;
    }, []);
    const formatPhone = useCallback((phoneCountryCode, phone = "") => {
        return `${phoneCountryCode ? `${phoneCountryCode} ` : ""}${phone}`;
    }, [phoneCodeOptions]);
    const formatGender = useCallback((genderTypeId) => {
        return gendersOptions.find((item) => item.value === genderTypeId)?.label || "";
    }, [gendersOptions]);
    const formatStartDate = useCallback((startDateValue) => {
        return startDatesOptions.find((item) => item.value === startDateValue)?.label || "";
    }, [startDatesOptions]);
    const formatGrade = useCallback((gradeId) => {
        return gradesOptions.find((item) => item.value === gradeId)?.label || "";
    }, [gradesOptions]);
    const formatPerformanceArea = useCallback((gradeId) => {
        return performanceAreasOptions.find((item) => item.value === gradeId)?.label || "";
    }, [performanceAreasOptions]);
    const formatLanguage = useCallback((languageCode) => {
        return languagesOptions.find((item) => item.value === languageCode)?.label || "";
    }, [languagesOptions]);
    const formatNationality = useCallback((nationalityCode) => {
        return nationalitiesOptions.find((item) => item.value === nationalityCode)?.label || "";
    }, [nationalitiesOptions]);
    const formatEthnicity = useCallback((ethnicityId) => {
        return ethnicitiesOptions.find((item) => item.value === ethnicityId)?.label || "-";
    }, [ethnicitiesOptions]);
    const formatCountry = useCallback((countryCode) => {
        return countriesOptions.find((item) => item.value === countryCode)?.label || "";
    }, [countriesOptions]);
    const formatRelationship = useCallback((relationshipId) => {
        return relationshipsOptions.find((item) => item.value === relationshipId)?.label || "";
    }, [relationshipsOptions]);
    const formatAddressMultiline = useCallback((address) => {
        return `${address.addressLine1}${address.addressLine2 ? `,\n${  address.addressLine2}` : ""},\n${address.area},\n${address.city},\n${address.code},\n${address.country}`;
    }, []);
    const formatAddress = useCallback((address) => {
        return `${address.addressLine1}${address.addressLine2 ? `, ${  address.addressLine2}` : ""}, ${address.city}, ${address.area}, ${address.code}, ${address.country}`;
    }, []);

    const handleEditParent = () => {
        navigate(`/${applicationId}/${ROUTES.PARENT}?edit`);
    };

    const handleEditStudent = (student) => {
        navigate(`/${applicationId}/${ROUTES.STUDENTS}/${student.childId}?edit`);
    };

    const handleSubmit = async () => {
        setShowError(true);
        if (termsChecked) {
            await submitApplication();
            clear();
            navigate(`/${ROUTES.COMPLETE}`);
        }
    };

    if (!leadData) return <Preloader />;

    return (
        <Preloader
            isLoading={isLoadingCountries || isLoadingGenders || isLoadingStartDates || isLoadingGrades || isLoadingPerformanceAreas || isLoadingLanguages || isLoadingNationalities || isLoadingEthnicities || isLoadingRelationships}
            overflow
        >
            <div className="ConfirmationPage">
                <Typography variant="h1" gutterBottom>
                    {t("confirmation.title")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                    {t("confirmation.subtitle")}
                </Typography>
                <InfoBox
                    title={t("parent.title")}
                    icon={<ConfirmationParentIcon />}
                    onEdit={handleEditParent}
                    className="mt-8"
                >
                    <Grid container spacing={0}>
                        <Grid item size={{xs: 12, md: 6}}>
                            <Typography variant="body2" component="div" gutterBottom>
                                <Typography variant="h4" component="span">
                                    {`${t("confirmation.parentName")} `}
                                </Typography>
                                {formatName(leadData)}
                            </Typography>
                            <Typography variant="body2" component="div" gutterBottom>
                                <Typography variant="h4" component="span">
                                    {`${t("confirmation.parentEmail")} `}
                                </Typography>
                                {leadData.emailAddress}
                            </Typography>
                            <Typography variant="body2" component="div" gutterBottom>
                                <Typography variant="h4" component="span">
                                    {`${t("parent.contactNumber")} `}
                                </Typography>
                                {formatPhone(leadData.phoneNumberCountryCode, leadData.phoneNumber)}
                            </Typography>
                        </Grid>
                        <Grid item size={{xs: 12, md: 6}}>
                            <Typography variant="h4" component="div" gutterBottom>
                                {`${t("confirmation.parentAddress")} `}
                            </Typography>
                            <Typography variant="body2" component="div" gutterBottom>
                                {formatAddressMultiline(leadData)}
                            </Typography>
                        </Grid>
                    </Grid>
                </InfoBox>
                {studentsData.map((student, idx) => (
                    <InfoBox
                        key={`${student.surname}-${student.forename}-${idx}`}
                        title={t("confirmation.studentN", {num: idx+1, name: formatName(student)})}
                        icon={<ConfirmationStudentIcon />}
                        onEdit={() => handleEditStudent(student)}
                        className="mt-8"
                    >
                        <Grid container spacing={0}>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.identity.preferredName")} `}
                                    </Typography>
                                    {student.preferredName || "-"}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.identity.gender")} `}
                                    </Typography>
                                    {formatGender(student.genderTypeId)}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.identity.dateOfBirth")} `}
                                    </Typography>
                                    {formatTo(student.dateOfBirth, "yyyy-MM-dd")}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.enrollment.gradeLevel")} `}
                                    </Typography>
                                    {formatGrade(student.notionalYearGroupId)}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.enrollment.desiredStartDate")} `}
                                    </Typography>
                                    {formatTo(student.desiredStartDate, "yyyy-MM-dd")}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.enrollment.mainPerformanceArea")} `}
                                    </Typography>
                                    {formatPerformanceArea(student.performanceAreaId)}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.background.nationality")} `}
                                    </Typography>
                                    {formatNationality(student.nationality)}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.background.firstLanguage")} `}
                                    </Typography>
                                    {formatLanguage(student.firstLanguage)}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.background.ethnicity")} `}
                                    </Typography>
                                    {(student.ethnicities || []).map((ethnicity) => formatEthnicity(ethnicity)).join(", ")}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.background.countryOfResidence")} `}
                                    </Typography>
                                    {formatCountry(student.countryOfResidence)}
                                </Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Typography variant="body2" component="div" gutterBottom>
                                    <Typography variant="h4" component="span">
                                        {`${t("students.address.relation")} `}
                                    </Typography>
                                    {formatRelationship(student.relationshipTypeId)}
                                </Typography>
                            </Grid>
                            <Grid item size={12}>
                                <div className="divider mt-4 mb-6" />
                                <Typography variant="h4" component="div" gutterBottom>
                                    {`${t("confirmation.studentAddress")} `}
                                </Typography>
                                <Typography variant="body2" component="div" gutterBottom>
                                    {formatAddress(student.address)}
                                </Typography>
                                {student.hasOtherAddress && (
                                    <div className="mt-2">
                                        <Typography variant="body2" component="div" gutterBottom>
                                            <Typography variant="h4" component="span">
                                                {`${t("confirmation.guardianName")} `}
                                            </Typography>
                                            {formatName({title: student.responsibleGuardian?.title, forename: student.responsibleGuardian.forename, surname: student.responsibleGuardian.surname})}
                                        </Typography>
                                        <Typography variant="body2" component="div" gutterBottom>
                                            <Typography variant="h4" component="span">
                                                {`${t("students.address.guardianEmail")} `}
                                            </Typography>
                                            {student.responsibleGuardian.email}
                                        </Typography>
                                        <Typography variant="body2" component="div" gutterBottom>
                                            <Typography variant="h4" component="span">
                                                {`${t("students.address.guardianPhone")} `}
                                            </Typography>
                                            {formatPhone(null, student.responsibleGuardian.phone)}
                                        </Typography>
                                        <Typography variant="body2" component="div" gutterBottom>
                                            <Typography variant="h4" component="span">
                                                {`${t("students.address.guardianRelation")} `}
                                            </Typography>
                                            {formatRelationship(student.responsibleGuardian.relationshipTypeId)}
                                        </Typography>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </InfoBox>
                ))}
                <Typography variant="body1" gutterBottom className="mt-8">
                    {t("confirmation.ensureText")}
                </Typography>
            </div>
            <div className="ConfirmationPage-submitBox">
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={termsChecked}
                            onChange={() => setTermsChecked(v => !v)}
                        />
                    )}
                    label={(
                        <>
                            {(showError && !termsChecked) && (
                                <span className="ConfirmationPage-submitBox__error">{t("confirmation.agreeError")}</span>
                            )}
                            {t("confirmation.agreeText")}
                        </>
                    )}
                />
                <Button
                    variant="accent"
                    type="submit"
                    className="w-full"
                    onClick={handleSubmit}
                    loading={isSubmittingApplication}
                >
                    {t("confirmation.submitApplication")}
                </Button>
            </div>
        </Preloader>
    );
};

export default ConfirmationPage;
